import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLang } from '@/utils/i18n';
import { ArrowLeft, Share2, ThumbsUp, ThumbsDown, Trophy, Clock, TrendingUp, MessageCircle } from 'lucide-react';
import LangSwitcher from '@/components/LangSwitcher';
import { MobileBottomNav } from '@/components/MobileNav';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http:\/\//i, 'https://');

const TipSharingPage = () => {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [tips, setTips] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('recent');
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadData(); }, [activeTab]);

  const loadData = async () => {
    try {
      const [tipsRes, lbRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/tip-sharing/community?sort=${activeTab}&limit=30`, { headers }).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/tip-sharing/leaderboard`, { headers }).then(r => r.json()),
      ]);
      setTips(tipsRes.tips || []);
      setLeaderboard(lbRes.leaderboard || []);
    } catch (e) {
      console.error('Load error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (sharedId, vote) => {
    try {
      await fetch(`${BACKEND_URL}/api/tip-sharing/vote`, {
        method: 'POST', headers,
        body: JSON.stringify({ shared_tip_id: sharedId, vote }),
      });
      loadData();
    } catch { /* ignore */ }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  const statusColors = {
    won: 'text-green-400 bg-green-500/10 border-green-500/30',
    lost: 'text-red-400 bg-red-500/10 border-red-500/30',
    pending: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center">
        <div className="animate-pulse text-[#00BFA6] text-xl">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121212] pb-16 md:pb-0" data-testid="tip-sharing-page">
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-gray-400 hover:text-white" data-testid="sharing-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> {t('back')}
          </Button>
          <h1 className="text-lg font-bold text-[#00BFA6] flex items-center gap-2">
            <Share2 className="w-5 h-5" /> Tip-Sharing
          </h1>
          <LangSwitcher />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
        {/* Tipster Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="mb-6" data-testid="tipster-leaderboard">
            <h2 className="text-sm font-bold text-[#00BFA6] uppercase tracking-wide mb-3 flex items-center gap-2">
              <Trophy className="w-4 h-4" /> Top Tipster
            </h2>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {leaderboard.slice(0, 5).map((l, i) => (
                <div key={i} className="shrink-0 px-4 py-3 bg-gradient-to-br from-[#00BFA6]/10 to-[#7B1FA2]/10 border border-[#00BFA6]/20 rounded-xl text-center min-w-[130px]">
                  <div className="text-xs text-gray-500">#{l.rank}</div>
                  <div className="text-sm font-bold text-white truncate mt-0.5">{l.anon_name}</div>
                  <div className="text-[#00BFA6] font-mono font-bold text-lg mt-1">{l.total_score}</div>
                  <div className="text-[10px] text-gray-500">{l.total_shared} geteilt</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sort Tabs */}
        <div className="mb-4 flex gap-2">
          {[
            { key: 'recent', label: lang === 'de' ? 'Neueste' : 'Recent', icon: <Clock className="w-3.5 h-3.5" /> },
            { key: 'top', label: 'Top', icon: <Trophy className="w-3.5 h-3.5" /> },
            { key: 'hot', label: 'Hot', icon: <TrendingUp className="w-3.5 h-3.5" /> },
          ].map(tab => (
            <button
              key={tab.key}
              data-testid={`sort-${tab.key}`}
              onClick={() => { setActiveTab(tab.key); setLoading(true); }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.key ? 'bg-[#00BFA6] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Shared Tips */}
        <div className="space-y-3" data-testid="shared-tips-list">
          {tips.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <Share2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>{lang === 'de' ? 'Noch keine geteilten Tipps. Teile deinen ersten!' : 'No shared tips yet. Share yours!'}</p>
            </div>
          ) : (
            tips.map((tip, i) => (
              <Card key={i} className="bg-[#1E1E1E] border-white/10 hover:border-white/20 transition-colors" data-testid={`shared-tip-${i}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-white">{tip.anon_name}</span>
                        <span className="text-[10px] text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {timeAgo(tip.shared_at)}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-white font-medium">{tip.team_home}</span>
                        <span className="text-gray-500 mx-2">vs</span>
                        <span className="text-white font-medium">{tip.team_away}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{tip.sport} - {tip.league}</div>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 rounded bg-[#00BFA6]/10 text-[#00BFA6] font-medium">{tip.prediction}</span>
                        <span className="text-xs font-mono text-gray-400">{tip.confidence}%</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${statusColors[tip.status] || statusColors.pending}`}>
                          {tip.status?.toUpperCase() || 'PENDING'}
                        </span>
                      </div>
                      {tip.comment && (
                        <div className="mt-2 flex items-start gap-1.5">
                          <MessageCircle className="w-3 h-3 text-gray-500 mt-0.5 shrink-0" />
                          <p className="text-xs text-gray-400 italic">"{tip.comment}"</p>
                        </div>
                      )}
                    </div>

                    {/* Vote buttons */}
                    <div className="flex flex-col items-center gap-1 shrink-0">
                      <button
                        data-testid={`vote-up-${i}`}
                        onClick={() => handleVote(tip.shared_id, 'up')}
                        className={`p-2 rounded-lg transition-all ${
                          tip.my_vote === 'up' ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-gray-500 hover:text-green-400 hover:bg-green-500/10'
                        }`}
                      >
                        <ThumbsUp className="w-4 h-4" />
                      </button>
                      <span className={`font-mono text-sm font-bold ${tip.score > 0 ? 'text-green-400' : tip.score < 0 ? 'text-red-400' : 'text-gray-500'}`}>
                        {tip.score || 0}
                      </span>
                      <button
                        data-testid={`vote-down-${i}`}
                        onClick={() => handleVote(tip.shared_id, 'down')}
                        className={`p-2 rounded-lg transition-all ${
                          tip.my_vote === 'down' ? 'bg-red-500/20 text-red-400' : 'bg-white/5 text-gray-500 hover:text-red-400 hover:bg-red-500/10'
                        }`}
                      >
                        <ThumbsDown className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default TipSharingPage;
